import { useState, type ReactNode } from 'react';
import { useT } from '../lib/i18n';
import { ActionError } from './states';
import { Button } from './ui/button';
import { Dialog } from './ui/dialog';
import { Input } from './ui/input';

/**
 * Bouton d'action à confirmer (spec 08 : aucune action destructive sans confirmation).
 * `expected` impose de saisir un mot exact (activation de l'envoi réel).
 */
export function ConfirmButton({
  label,
  title,
  message,
  onConfirm,
  variant = 'outline',
  expected,
  disabled,
  children,
}: {
  label: string;
  title: string;
  message: string;
  onConfirm: () => Promise<unknown>;
  variant?: 'default' | 'outline' | 'destructive' | 'secondary';
  expected?: string;
  disabled?: boolean;
  children?: ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const [pending, setPending] = useState(false);

  async function confirm() {
    setPending(true);
    setError(null);
    try {
      await onConfirm();
      setOpen(false);
      setTyped('');
    } catch (err) {
      setError(err as Error);
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title={title}
      description={message}
      trigger={
        <Button variant={variant} size="sm" disabled={disabled}>
          {label}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {children}
        {expected ? (
          <Input
            aria-label={expected}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
          />
        ) : null}
        <ActionError error={error} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            {t.common.cancel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            disabled={pending || (expected !== undefined && typed !== expected)}
            onClick={() => void confirm()}
          >
            {t.common.confirm}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
